import React from "react";

import { flattenTree } from "@common/components/Tree";
import { FolderListDto } from "@common/dto/list-folder";

import { ImportQuestionnaireManagementProps } from "./import-questionnaire-management.types";

export const useController = (props: ImportQuestionnaireManagementProps) => {
  const { onSubmit, importedQuestionnaireFile, folders } = props;
  const [allowImportToCreateNewFields, setAllowImportToCreateNewFields] = React.useState(false);
  const [selectedFoldersIds, setSelectedFoldersIds] = React.useState<Array<FolderListDto["id"]>>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [renameQuestionnaireManagementOpened, setRenameQuestionnaireManagementOpened] = React.useState(false);

  const openRenameQuestionnaireManagement = React.useCallback(() => {
    setRenameQuestionnaireManagementOpened(true);
  }, []);

  const closeRenameQuestionnaireManagement = React.useCallback(() => {
    setRenameQuestionnaireManagementOpened(false);
  }, []);

  const checkItIsNotFakeParentId = React.useCallback((id: number | string) => Number(id) > 0, []);

  const flattenTreeFolders = React.useMemo(() => flattenTree(folders), [folders]);

  const foldersIds = React.useMemo(() => {
    return flattenTreeFolders
      .map(({ id }) => id)
      .filter(checkItIsNotFakeParentId);
  }, [flattenTreeFolders]);

  const processedSelectedFolderIds = React.useMemo(() => selectedFoldersIds.filter(checkItIsNotFakeParentId), [selectedFoldersIds]);

  const allFoldersSelected =
    Boolean(processedSelectedFolderIds.length) &&
    foldersIds.length === processedSelectedFolderIds.filter(checkItIsNotFakeParentId).length;

  const indeterminateStateForSelectAllCheckbox = !allFoldersSelected && processedSelectedFolderIds.length > 0;

  const handleSelectAllFolder = React.useCallback(
    (isSelected: boolean) => {
      setSelectedFoldersIds(isSelected ? foldersIds : []);
    },
    [foldersIds]
  );

  const importedQuestionnaireFilename = React.useMemo(() => importedQuestionnaireFile.name.slice(0, importedQuestionnaireFile.name.lastIndexOf(".")), [importedQuestionnaireFile]);

  const handleSaveImportedQuestionnaire = async (questionnaireName?: string) => {
      if (!questionnaireName) {
        return;
      }

      setSubmitting(true);

      const importQuestionnaireResponse = await onSubmit({
        file: importedQuestionnaireFile,
        name: questionnaireName,
        createFields: allowImportToCreateNewFields,
        fieldIds: processedSelectedFolderIds.length ? processedSelectedFolderIds : [],
      });

      const isDuplicatedNameError = importQuestionnaireResponse?.errors?.length;

      if(isDuplicatedNameError) {
        openRenameQuestionnaireManagement();
      }
      setSubmitting(false);
    };
  
  const handleSubmit = React.useCallback(async () => {
      handleSaveImportedQuestionnaire(importedQuestionnaireFilename);
    }, [handleSaveImportedQuestionnaire, importedQuestionnaireFilename]);

  const handleSubmitRenameQuestionnaire = React.useCallback(
    (questionnaireName: string) => {
      closeRenameQuestionnaireManagement();
      handleSaveImportedQuestionnaire(questionnaireName);
    },
    [closeRenameQuestionnaireManagement, handleSaveImportedQuestionnaire]
  );

  return {
    handleSelectAllFolder,
    handleSubmit,
    closeRenameQuestionnaireManagement,
    setAllowImportToCreateNewFields,
    handleSubmitRenameQuestionnaire,
    setSelectedFoldersIds,
    indeterminateStateForSelectAllCheckbox,
    importedQuestionnaireFilename,
    allowImportToCreateNewFields,
    submitting,
    renameQuestionnaireManagementOpened,
    folders,
    selectedFoldersIds,
    foldersIds,
    allFoldersSelected,
  };
};
